import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, fmtEur } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { PrintButton, PrintHeader } from "@/components/PrintList";

const ESTADOS = [
  { id: "HECHO", label: "Hechos", cls: "bg-slate-100 text-slate-700" },
  { id: "ENVIADO", label: "Enviados", cls: "bg-blue-50 text-blue-800" },
  { id: "APROBADO", label: "Aprobados", cls: "bg-emerald-50 text-emerald-800" },
  { id: "DUDOSO", label: "Dudosos", cls: "bg-amber-50 text-amber-800" },
  { id: "RECHAZADO", label: "Rechazados", cls: "bg-rose-50 text-rose-800" },
];

const Chip = ({ e, list, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`ppto-tab-${e.id}`}
    className={`lift text-left rounded-lg border px-4 py-3 transition-colors ${active ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-white border-slate-200 hover:border-slate-400"}`}>
    <div className="font-[JetBrains_Mono] text-2xl font-semibold">{list.length}</div>
    <div className={`text-[11px] ${active ? "text-blue-100" : "text-slate-600"}`}>{e.label} · {fmtEur(list.reduce((s, b) => s + (b.total || 0), 0))}</div>
  </button>
);

const Row = ({ b, onEstado }) => (
  <tr data-testid={`ppto-row-${b.id}`} className="hover:bg-slate-50">
    <td className="px-3 py-2 font-[JetBrains_Mono] text-xs whitespace-nowrap">{fmtDate(b.fecha)}</td>
    <td className="px-3 py-2"><Link to={`/pacientes/${b.patient_id}`} className="font-medium text-slate-900 hover:underline">{b.patient.nombre}</Link></td>
    <td className="px-3 py-2 text-xs text-slate-500">{b.patient.centro}<span className="print-only">{b.patient.telefono}</span></td>
    <td className="px-3 py-2 text-xs text-slate-500 max-w-[320px] truncate">{(b.lineas || []).map((l) => l.concepto).join(", ")}</td>
    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{fmtEur(b.total)}</td>
    <td className="px-3 py-2">
      <select data-testid={`ppto-row-estado-${b.id}`} className="field-select h-8 text-xs py-0" value={b.estado} onChange={(e) => onEstado(b, e.target.value)}>
        {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.id}</option>)}
      </select>
    </td>
    <td className="px-3 py-2 no-print"><a href={`/pacientes/${b.patient_id}/presupuesto?id=${b.id}`} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] hover:underline" data-testid={`ppto-open-${b.id}`}>Abrir</a></td>
  </tr>
);

export default function Presupuestos() {
  const { centro } = useCentro();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState("");
  const load = useCallback(() => {
    api.get("/presupuestos", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);
  const onEstado = async (b, estado) => {
    const { data } = await api.put(`/presupuestos/${b.id}`, { ...b, estado });
    toast.success(`Presupuesto ${estado.toLowerCase()}`);
    if (estado === "RECHAZADO") toast.info("Ficha marcada como No aprobado");
    if (estado === "APROBADO") toast.info(data.created_treatments?.length ? `Ficha marcada como aprobada. Tratamientos creados: ${data.created_treatments.join(", ")}` : "Ficha marcada como aprobada");
    load();
  };
  const list = (rows || []).filter((b) => !sel || b.estado === sel);
  return (
    <div data-testid="presupuestos-page">
      <div className="no-print"><PageTitle title="Presupuestos" subtitle="Se guardan al pulsar Guardar, Imprimir o WhatsApp en el presupuesto de cada paciente"><PrintButton /></PageTitle></div>
      <PrintHeader title={sel ? `Presupuestos ${ESTADOS.find((e) => e.id === sel).label.toLowerCase()}` : "Presupuestos"} detalle={`Total ${fmtEur(list.reduce((s, b) => s + (b.total || 0), 0))}`} n={list.length} />
      <div className="no-print grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
        <Chip e={{ id: "todos", label: "Todos" }} list={rows || []} active={!sel} onClick={() => setSel("")} />
        {ESTADOS.map((e) => <Chip key={e.id} e={e} list={(rows || []).filter((b) => b.estado === e.id)} active={sel === e.id} onClick={() => setSel(sel === e.id ? "" : e.id)} />)}
      </div>
      {rows === null && <p className="text-sm text-slate-400">Cargando…</p>}
      {rows && list.length === 0 && <p className="text-sm text-slate-400" data-testid="ppto-empty-list">No hay presupuestos aquí.</p>}
      {list.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">{list.map((b) => <Row key={b.id} b={b} onEstado={onEstado} />)}</tbody></table>
        </div>
      )}
    </div>
  );
}
